import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { OptimizedPhoto } from '../hooks/useOptimizedPhotos';

/**
 * 优化的全屏图片预览组件
 * 使用 Portal 渲染到 body，按需加载原图
 */
interface OptimizedPhotoViewerProps {
  photo: OptimizedPhoto | null;
  onClose: () => void;
  getFullImageUrl: (photoId: string) => Promise<string>;
}

const OptimizedPhotoViewer: React.FC<OptimizedPhotoViewerProps> = ({
  photo,
  onClose,
  getFullImageUrl
}) => {
  const [fullImageUrl, setFullImageUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // 加载原图
  useEffect(() => {
    if (!photo) {
      setFullImageUrl(null);
      setIsLoading(false);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    // 先显示缩略图，再加载原图
    setFullImageUrl(photo.thumbnailUrl);

    getFullImageUrl(photo.id)
      .then(url => {
        setFullImageUrl(url);
        setIsLoading(false);
      })
      .catch(err => {
        console.error('[OptimizedPhotoViewer] 加载原图失败:', err);
        setError('图片加载失败');
        setIsLoading(false);
      });
  }, [photo, getFullImageUrl]);

  // 按 ESC 键关闭
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (photo) {
      window.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [photo, onClose]);

  // 阻止滚动穿透
  useEffect(() => {
    if (photo) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [photo]);

  if (!photo) return null;

  const viewerContent = (
    <div
      className="fixed inset-0 z-50 bg-black bg-opacity-90 flex items-center justify-center"
      onClick={onClose}
    >
      {/* 关闭按钮 */}
      <button
        className="absolute top-4 right-4 text-white text-3xl w-10 h-10 flex items-center justify-center rounded-full bg-black bg-opacity-50 hover:bg-opacity-70 transition-opacity z-10"
        onClick={onClose}
      >
        ×
      </button>

      {/* 图片容器 */}
      <div
        className="relative max-w-full max-h-full p-4"
        onClick={(e) => e.stopPropagation()}
      >
        {fullImageUrl && (
          <img
            src={fullImageUrl}
            alt={photo.trackingNumber || 'Photo'}
            className="max-w-full max-h-[90vh] object-contain"
          />
        )}

        {/* 加载指示器 */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="bg-black bg-opacity-50 rounded-lg px-4 py-2 text-white text-sm">
              加载原图中...
            </div>
          </div>
        )}

        {/* 错误提示 */}
        {error && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="bg-red-500 bg-opacity-90 rounded-lg px-4 py-2 text-white text-sm">
              {error}
            </div>
          </div>
        )}

        {/* 包裹号（仅 label 类型显示） */}
        {photo.trackingNumber && (
          <div className="absolute bottom-4 left-4 right-4 bg-black bg-opacity-60 text-white text-sm px-3 py-2">
            {photo.trackingNumber}
          </div>
        )}
      </div>
    </div>
  );

  // 使用 Portal 渲染到 body
  return createPortal(viewerContent, document.body);
};

export default OptimizedPhotoViewer;
